<!-- Page header -->
<div class="flex flex-row justify-between items-center mb-3">
  <div class="flex flex-col">
    <h1 class="headeradminui-text md:break-keep">Global Policy Rules</h1>
    <div class="text-xs text-gray-500 mt-1">
      Platform-wide defaults applied to all partners. Company rules override these by setting a higher priority.
    </div>
  </div>
</div>

<nz-spin [nzSpinning]="loading" nzTip="Loading...">
  <div class="custom-card">
    <div class="card-body p-4">

      <!-- Section header -->
      <div class="flex items-start justify-between gap-4 mb-3">
        <div>
          <div class="flex items-center gap-2">
            <div class="text-base font-semibold">Global Rules</div>
            <nz-tag nzColor="purple">Global · Platform defaults</nz-tag>
            <nz-tag nzColor="blue">{{ activeRulesCount }} active · {{ rulesUI.length }} total</nz-tag>
          </div>
          <div class="text-sm text-gray-500 mt-1">
            These rules apply across all companies unless overridden by a company rule with higher priority.
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        *ngIf="!loading && rulesUI.length === 0"
        class="text-center py-8 text-gray-400 text-sm border border-gray-200 rounded-lg bg-white mb-2"
      >
        No global rules yet. Add one below.
      </div>

      <!-- Rule cards -->
      <div
        *ngFor="let rule of rulesUI"
        class="border rounded-lg mb-1.5 overflow-hidden transition-colors"
        [style.border-color]="rule.expanded ? '#1890ff' : '#e8e8e8'"
      >
        <!-- Collapsed header — click to expand -->
        <div
          class="flex items-center gap-2 px-4 py-2.5 cursor-pointer select-none min-h-[46px]"
          [ngClass]="rule.expanded ? 'bg-blue-50' : 'hover:bg-gray-50'"
          (click)="toggleExpand(rule)"
        >
          <!-- Priority pill -->
          <span
            class="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
            style="background:#f5f5f5;border:1px solid #e0e0e0;color:#666;min-width:32px;text-align:center"
          >P{{ rule.priority }}</span>

          <!-- Global tag -->
          <nz-tag nzColor="purple" style="margin:0;flex-shrink:0">Global</nz-tag>

          <!-- Action badge -->
          <span
            class="text-xs font-medium px-2 py-0.5 rounded flex-shrink-0"
            style="background:#f5f5f5;color:#555"
          >{{ rule.action }}</span>

          <!-- Scope -->
          <span class="text-xs font-medium text-gray-700 truncate" style="max-width:120px">
            {{ rule._scopeSummary }}
          </span>
          <span class="text-gray-300 flex-shrink-0">·</span>

          <!-- Condition summary -->
          <span
            class="text-xs px-2 py-0.5 rounded flex-shrink-0 truncate"
            style="background:#f9fafb;border:1px solid #e8ecf4;color:#6b7280;max-width:200px"
          >{{ rule._condSummary }}</span>
          <span class="text-gray-300 flex-shrink-0">→</span>

          <!-- Then summary -->
          <span
            class="text-xs px-2 py-0.5 rounded flex-shrink-0 truncate"
            style="background:#f0fdf4;border:1px solid #bbf7d0;color:#166534;max-width:200px"
          >{{ rule._thenSummary }}</span>

          <div class="flex-1"></div>

          <!-- Active badge -->
          <nz-tag [nzColor]="rule.active ? 'green' : 'default'" style="margin:0;flex-shrink:0">
            {{ rule.active ? 'Active' : 'Off' }}
          </nz-tag>

          <!-- Delete -->
          <button
            nz-button nzType="link" nzDanger nzSize="small"
            style="padding:0 4px;flex-shrink:0"
            (click)="$event.stopPropagation(); deleteRule(rule)"
          >✕</button>

          <!-- Chevron -->
          <span class="text-gray-400 text-xs flex-shrink-0 ml-1">
            {{ rule.expanded ? '▲' : '▼' }}
          </span>
        </div>

        <!-- Expanded body -->
        <div *ngIf="rule.expanded" class="border-t border-gray-100">

          <!-- FOR zone -->
          <div class="p-4 border-b border-gray-100">
            <div class="text-xs font-bold uppercase tracking-wider mb-3" style="color:#9ca3af">
              FOR — what does this rule apply to?
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label class="block mb-1 text-xs text-gray-500 font-medium">Action</label>
                <nz-select [(ngModel)]="rule.action" class="w-full" (ngModelChange)="onActionChange(rule)">
                  <nz-option *ngFor="let a of ACTIONS" [nzValue]="a" [nzLabel]="a"></nz-option>
                </nz-select>
              </div>
              <ng-container *ngIf="rule.action === 'Session Booking'">
                <div>
                  <label class="block mb-1 text-xs text-gray-500 font-medium">
                    Vertical <span class="text-gray-300 italic font-normal">optional</span>
                  </label>
                  <nz-select [(ngModel)]="rule.scope.verticalId" class="w-full" nzMode="multiple" nzAllowClear nzPlaceholder="Any vertical" (ngModelChange)="onScopeChange(rule)">
                    <nz-option *ngFor="let v of verticals" [nzValue]="v.m_id" [nzLabel]="v.data"></nz-option>
                  </nz-select>
                </div>
                <div>
                  <label class="block mb-1 text-xs text-gray-500 font-medium">
                    Concern <span class="text-gray-300 italic font-normal">optional</span>
                  </label>
                  <nz-select [(ngModel)]="rule.scope.concernId" class="w-full" nzMode="multiple" nzAllowClear nzPlaceholder="Any concern" nzShowSearch>
                    <nz-option *ngFor="let c of concernsForRule(rule)" [nzValue]="c.m_id" [nzLabel]="c.data"></nz-option>
                  </nz-select>
                </div>
                <div>
                  <label class="block mb-1 text-xs text-gray-500 font-medium">
                    Expert level <span class="text-gray-300 italic font-normal">optional</span>
                  </label>
                  <nz-select [(ngModel)]="rule.scope.expertLevelId" class="w-full" nzAllowClear nzPlaceholder="Any level">
                    <nz-option *ngFor="let e of expertLevels" [nzValue]="e.m_id" [nzLabel]="e.data"></nz-option>
                  </nz-select>
                </div>
                <div>
                  <label class="block mb-1 text-xs text-gray-500 font-medium">
                    Experts <span class="text-gray-300 italic font-normal">optional — one or more named therapists</span>
                  </label>
                  <nz-select [(ngModel)]="rule.scope.therapistIds" class="w-full" nzMode="multiple" nzAllowClear nzShowSearch nzPlaceholder="Any expert">
                    <nz-option *ngFor="let t of therapists" [nzValue]="t._id" [nzLabel]="therapistDisplayName(t._id)"></nz-option>
                  </nz-select>
                </div>
              </ng-container>
              <div>
                <label class="block mb-1 text-xs text-gray-500 font-medium">Priority</label>
                <nz-input-number [(ngModel)]="rule.priority" [nzMin]="0" class="w-full"></nz-input-number>
                <div class="text-xs text-gray-400 mt-1">10 = low, 100+ = high. Company rules override by going higher.</div>
              </div>
              <div class="flex items-center justify-between gap-2 p-3 rounded" style="background:#f9fafb">
                <span class="text-sm font-medium">Active</span>
                <nz-switch [(ngModel)]="rule.active"></nz-switch>
              </div>
              <div class="md:col-span-2">
                <label class="block mb-1 text-xs text-gray-500 font-medium">Rule name</label>
                <input nz-input [(ngModel)]="rule.name" placeholder="e.g. Default corporate sessions" />
              </div>
            </div>
          </div>

          <!-- WHEN zone -->
          <div class="p-4 border-b border-gray-100">
            <div class="text-xs font-bold uppercase tracking-wider mb-3" style="color:#9ca3af">
              WHEN — condition
            </div>

            <!-- Mode toggle -->
            <div class="flex gap-2 mb-3">
              <button
                class="px-3 py-1 rounded text-xs cursor-pointer border transition-all"
                [style.border-color]="rule.conditionMode === 'always' ? '#1890ff' : '#d9d9d9'"
                [style.color]="rule.conditionMode === 'always' ? '#1890ff' : '#6b7280'"
                [style.background]="rule.conditionMode === 'always' ? '#e6f4ff' : '#fff'"
                [style.font-weight]="rule.conditionMode === 'always' ? '500' : '400'"
                (click)="rule.conditionMode = 'always'"
              >Always applies (no conditions)</button>
              <button
                class="px-3 py-1 rounded text-xs cursor-pointer border transition-all"
                [style.border-color]="rule.conditionMode === 'when' ? '#1890ff' : '#d9d9d9'"
                [style.color]="rule.conditionMode === 'when' ? '#1890ff' : '#6b7280'"
                [style.background]="rule.conditionMode === 'when' ? '#e6f4ff' : '#fff'"
                [style.font-weight]="rule.conditionMode === 'when' ? '500' : '400'"
                (click)="rule.conditionMode = 'when'"
              >Conditional</button>
            </div>

            <!-- Condition rows -->
            <ng-container *ngIf="rule.conditionMode === 'when'">

              <!-- Logic selector (multi-cond only) -->
              <div class="flex items-center gap-2 mb-2" *ngIf="rule.condRows.length > 1">
                <span class="text-xs text-gray-500">Match</span>
                <button
                  class="px-2 py-0.5 rounded text-xs cursor-pointer border transition-all"
                  [style.border-color]="rule.condLogic === 'all' ? '#1890ff' : '#d9d9d9'"
                  [style.color]="rule.condLogic === 'all' ? '#1890ff' : '#6b7280'"
                  [style.background]="rule.condLogic === 'all' ? '#e6f4ff' : '#fff'"
                  (click)="rule.condLogic = 'all'"
                >ALL of</button>
                <button
                  class="px-2 py-0.5 rounded text-xs cursor-pointer border transition-all"
                  [style.border-color]="rule.condLogic === 'any' ? '#1890ff' : '#d9d9d9'"
                  [style.color]="rule.condLogic === 'any' ? '#1890ff' : '#6b7280'"
                  [style.background]="rule.condLogic === 'any' ? '#e6f4ff' : '#fff'"
                  (click)="rule.condLogic = 'any'"
                >ANY of</button>
                <span class="text-xs text-gray-500">the conditions below</span>
              </div>

              <!-- Condition row list -->
              <div
                *ngFor="let cond of rule.condRows; let i = index"
                class="flex items-center gap-2 mb-2 p-2 rounded"
                style="background:#fafafa;border:1px solid #f0f0f0"
              >
                <!-- Fact select -->
                <nz-select
                  [(ngModel)]="cond.fact"
                  style="min-width:200px"
                  [nzDropdownMatchSelectWidth]="false"
                  (ngModelChange)="onFactChange(rule, cond)"
                >
                  <nz-option
                    *ngFor="let f of factsFor(rule.action, rule)"
                    nzCustomContent
                    [nzValue]="f.value"
                    [nzLabel]="f.label"
                  >
                    <div style="pointer-events:none">{{ f.label }}</div>
                    <small *ngIf="f.description" style="color:#8c8c8c;white-space:normal;line-height:1.3;display:block;max-width:320px;pointer-events:none">{{ f.description }}</small>
                  </nz-option>
                </nz-select>

                <!-- Days param input for sessionsInLastNDays -->
                <ng-container *ngIf="cond.fact === 'sessionsInLastNDays' || cond.fact === 'totalSessionsInLastNDays'">
                  <span class="text-xs text-gray-500 flex-shrink-0">in last</span>
                  <nz-input-number [(ngModel)]="cond.params!['days']" [nzMin]="1" style="width:72px" nzPlaceHolder="7"></nz-input-number>
                  <span class="text-xs text-gray-500 flex-shrink-0">days</span>
                </ng-container>

                <!-- Operator (number facts) -->
                <nz-select
                  *ngIf="factType(rule.action, cond.fact) === 'number'"
                  [(ngModel)]="cond.operator"
                  style="width:64px"
                >
                  <nz-option
                    *ngFor="let op of NUM_OPS"
                    [nzValue]="op.value"
                    [nzLabel]="op.label"
                  ></nz-option>
                </nz-select>

                <!-- Value: number -->
                <nz-input-number
                  *ngIf="factType(rule.action, cond.fact) === 'number'"
                  [(ngModel)]="cond.value"
                  [nzMin]="0"
                  style="width:100px"
                ></nz-input-number>

                <!-- Value: bool -->
                <nz-select
                  *ngIf="factType(rule.action, cond.fact) === 'bool'"
                  [(ngModel)]="cond.value"
                  style="width:120px"
                >
                  <nz-option [nzValue]="true" nzLabel="Yes"></nz-option>
                  <nz-option [nzValue]="false" nzLabel="No"></nz-option>
                </nz-select>

                <button
                  nz-button nzType="link" nzDanger nzSize="small"
                  style="padding:0 4px;flex-shrink:0"
                  (click)="removeCond(rule, i)"
                >✕</button>
              </div>

              <!-- Empty cond state -->
              <div
                *ngIf="rule.condRows.length === 0"
                class="text-xs text-gray-400 py-2"
              >
                No conditions added — click "+ Add condition" below.
              </div>

              <button
                class="mt-1 px-3 py-1 rounded text-xs cursor-pointer border transition-all hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50"
                style="border:1px dashed #d9d9d9;background:#fff;color:#9ca3af"
                (click)="addCond(rule)"
              >+ Add condition</button>
            </ng-container>
          </div>

          <!-- THEN zone -->
          <div class="p-4 border-b border-gray-100">
            <div class="text-xs font-bold uppercase tracking-wider mb-3" style="color:#9ca3af">
              THEN — outputs
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ng-container *ngFor="let out of outputsFor(rule.action)">
                <div [hidden]="!isFieldVisible(rule, out)">
                  <label class="block mb-1 text-xs text-gray-500 font-medium">{{ out.label }}</label>
                  <!-- Select output -->
                  <nz-select
                    *ngIf="out.type === 'select'"
                    [(ngModel)]="rule.outputs[out.key]"
                    class="w-full"
                    nzPlaceholder="(not set)"
                    nzAllowClear
                    (ngModelChange)="onSelfPayOutputChange(rule, out.key)"
                  >
                    <nz-option
                      *ngFor="let opt of out.options"
                      [nzValue]="opt.v"
                      [nzLabel]="opt.l"
                    ></nz-option>
                  </nz-select>
                  <!-- Number output -->
                  <nz-input-number
                    *ngIf="out.type === 'number'"
                    [(ngModel)]="rule.outputs[out.key]"
                    [nzMin]="0"
                    nzPlaceholder="(not set)"
                    class="w-full"
                  ></nz-input-number>
                  <!-- Text output -->
                  <input
                    *ngIf="out.type === 'text'"
                    nz-input
                    [(ngModel)]="rule.outputs[out.key]"
                    placeholder="e.g. Your company's therapy sessions for this quarter have been used up."
                  />
                </div>
              </ng-container>

              <div *ngIf="outputsFor(rule.action).length === 0" class="md:col-span-2 text-xs text-gray-400">
                No outputs defined for this action.
              </div>
            </div>

            <!-- Flat-slab price-by-duration row editor — only when payer=user and pricingMode=flatSlab -->
            <div *ngIf="showFlatSlabEditor(rule)" class="mt-3 pt-3 border-t border-gray-100">
              <div class="text-xs font-medium text-gray-500 mb-2">Price by session duration (minutes)</div>
              <div *ngFor="let row of flatSlabRows(rule); let i = index" class="flex items-center gap-2 mb-2">
                <nz-input-number [(ngModel)]="row.minDuration" [nzMin]="0" style="width:110px" nzPlaceHolder="min (incl.)"></nz-input-number>
                <span class="text-xs text-gray-400">to</span>
                <nz-input-number [(ngModel)]="row.maxDuration" [nzMin]="0" style="width:110px" nzPlaceHolder="max (excl.)"></nz-input-number>
                <span class="text-xs text-gray-400">min →</span>
                <nz-input-number [(ngModel)]="row.amount" [nzMin]="0" style="width:120px" nzPlaceHolder="₹ amount"></nz-input-number>
                <button nz-button nzType="link" nzDanger nzSize="small" style="padding:0 4px" (click)="removeFlatSlabRow(rule, i)">✕</button>
              </div>
              <button nz-button nzType="link" nzSize="small" (click)="addFlatSlabRow(rule)">+ Add duration price</button>
            </div>
          </div>

          <!-- Footer actions -->
          <div class="flex justify-end gap-2 p-4">
            <button nz-button nzType="default" (click)="rule.expanded = false">Cancel</button>
            <button nz-button nzType="primary" [nzLoading]="rule.saving" (click)="saveRule(rule)">
              Save Global Rule
            </button>
          </div>
        </div>
      </div>

      <!-- Add button -->
      <button
        class="w-full mt-2 py-2 px-4 border-dashed border rounded-lg text-sm text-gray-400 cursor-pointer hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
        style="border-color:#d9d9d9; background:transparent"
        (click)="addRule()"
      >
        + Add global rule
      </button>

    </div>
  </div>
</nz-spin>
